import { ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import { parseStats, type AgentStats } from './aiAgentContract'

/**
 * useAgentStats — the Full-only aggregate-stats composable for the assistant.
 *
 * Reads the agent's aggregate diagnostics via GET `/admin/stats` on the
 * ai-agent-service (`config.api.agentHost`). The route is Full-only, enforced
 * SERVER-SIDE (the backend checks the caller's Cognito `Full` group); the
 * frontend route guard is UX-only and not authoritative.
 *
 * SECURITY INVARIANT: stats expose counts / latencies / outcomes ONLY — never
 * message content or any PII. The boundary guard (`parseStats`) builds the
 * result from only those fields, so a future backend leak would not surface here.
 *
 * Network access lives ONLY here (never in the .vue), honoring the
 * `no_httpservice_in_vue` qa rule and CLAUDE.md rules 3/5. Absolute URL → the
 * Bearer JWT is attached by httpService at the transport layer (never a param).
 *
 * `skipErrorDisplay` so this composable owns the error surface (`error.value`);
 * PT messages mapped here, raw error text kept in the structured log only.
 */
export function useAgentStats() {
  const { t } = useI18n()
  const stats: Ref<AgentStats | null> = ref(null)
  const loading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)

  function ptError(err: unknown): string {
    const status = (err as HttpError)?.status
    if (status === 401) {
      return t('Sua sessão expirou. Faça login novamente.')
    }
    if (status === 403) {
      return t('Você não tem permissão para ver as estatísticas do assistente.')
    }
    return t('Erro ao carregar as estatísticas do assistente. Tente novamente.')
  }

  async function load(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const raw: unknown = await httpService.get(`${config.api.agentHost}/admin/stats`, {
        skipErrorDisplay: true,
        logContext: 'useAgentStats',
      })
      stats.value = parseStats(raw)
      log('useAgentStats', 'load', 'Estatísticas carregadas', {
        chatCount: stats.value.chatCount,
        messageCount: stats.value.messageCount,
        userCount: stats.value.userCount,
      })
    } catch (err) {
      error.value = ptError(err)
      const message = err instanceof Error ? err.message : String(err)
      log('useAgentStats', 'load', 'Falha ao carregar estatísticas', {
        message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    stats,
    loading,
    error,
    load,
  }
}
